import type { PropsWithChildren } from 'react';
import React from 'react';
import { Circle, OverlayView, OverlayViewF } from '@react-google-maps/api';
import { useTranslation } from 'next-i18next';

import Box from '@mui/material/Box';

import type { GoogleMapComponentProps } from 'src/components/map/GoogleMap';
import GoogleMap from 'src/components/map/GoogleMap';
import Icon from 'src/components/ui/Icon';

import {
    francePosition,
    approximateZoom,
    inaccurateZoom,
    defaultZoom,
    inaccurateRadius,
    isHiddenRadius,
    circleOptions,
} from 'src/constants/googleMap';
import { styledComponent } from 'src/utils/styled';
import { colours } from 'src/constants/colours';
import theme from 'src/utils/theme';

const StyledPinHouseIcon = styledComponent(Icon)`
    margin-top: -3rem;
    transform: translate(-50%);
`;

const InaccurateLabel = styledComponent(Box)`
    font-size: 1rem;
    background-color: ${theme.palette.primary.main};
    color: ${colours.white.main};
    border-radius: 0.25rem;
    padding: 0.25rem 0.5rem;
    white-space: nowrap;
`;

const CustomMarker = ({ inaccurate }: { inaccurate?: boolean }) => {
    const { t } = useTranslation(['ad']);
    return (
        <div>
            <StyledPinHouseIcon name="PinHouse" size="3rem" />
            {inaccurate && (
                <InaccurateLabel width="fit-content" style={{ transform: 'translate(-50%, 600%)' }}>
                    {t('ad:inaccurateAddress')}
                </InaccurateLabel>
            )}
        </div>
    );
};

type Props = {
    approximate?: boolean;
    inaccurate?: boolean;
    point?: google.maps.LatLngLiteral;
} & GoogleMapComponentProps;

const GoogleMapWithMarker: React.FC<PropsWithChildren<Props>> = ({ approximate, inaccurate, point, zoom, ...rest }) => {
    let mapZoom = zoom ?? defaultZoom;
    if (approximate) {
        mapZoom = approximateZoom;
    }
    if (inaccurate) {
        mapZoom = inaccurateZoom;
    }

    return (
        <GoogleMap center={point ?? francePosition.point} zoom={mapZoom} {...rest}>
            {point && (
                <OverlayViewF position={point} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                    <CustomMarker inaccurate={inaccurate} />
                </OverlayViewF>
            )}
            {point && (approximate || inaccurate) && (
                <Circle
                    center={{
                        lat: point.lat,
                        lng: point.lng,
                    }}
                    radius={inaccurate ? inaccurateRadius : isHiddenRadius}
                    options={circleOptions}
                />
            )}
        </GoogleMap>
    );
};

export default GoogleMapWithMarker;
